import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { dashboardService } from '@services/dashboardService';
import adminConsoleService from '@services/adminConsoleService';
import { STORAGE_KEYS } from '@lib/constants';
import { js } from '@config/env';

function resolveBrandImageUrl(path: string): string
{
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = String(js.settings.apiAuthURL || '').replace(/\/?$/, '/');
  return base + path.replace(/^\//, '');
}

type Shape = 'circle' | 'square' | 'rectangle';

function shapeToCssRadius(shape: Shape): string
{
  return shape === 'circle' ? '50%' : '0%';
}

// ─── Cropper types & constants ────────────────────────────────────────────────

const CROP_W = 420;
const CROP_H = 300;
const MIN_SIZE = 32;

interface CropRect { x: number; y: number; w: number; h: number }
type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

interface ImgMeta {
  natW: number; natH: number;
  dispX: number; dispY: number;
  dispW: number; dispH: number;
}

interface DragState {
  mode: 'move' | 'resize';
  handle: HandleId | null;
  startMX: number; startMY: number;
  startCrop: CropRect;
}

const HANDLES: Array<{ id: HandleId; style: React.CSSProperties; cursor: string }> = [
  { id: 'nw', style: { top: -5, left: -5 }, cursor: 'nwse-resize' },
  { id: 'n',  style: { top: -5, left: '50%', marginLeft: -5 }, cursor: 'ns-resize' },
  { id: 'ne', style: { top: -5, right: -5 }, cursor: 'nesw-resize' },
  { id: 'e',  style: { top: '50%', right: -5, marginTop: -5 }, cursor: 'ew-resize' },
  { id: 'se', style: { bottom: -5, right: -5 }, cursor: 'nwse-resize' },
  { id: 's',  style: { bottom: -5, left: '50%', marginLeft: -5 }, cursor: 'ns-resize' },
  { id: 'sw', style: { bottom: -5, left: -5 }, cursor: 'nesw-resize' },
  { id: 'w',  style: { top: '50%', left: -5, marginTop: -5 }, cursor: 'ew-resize' },
];

// ─── Cropper helpers ──────────────────────────────────────────────────────────

function computeImgMeta(natW: number, natH: number): ImgMeta
{
  const scale = Math.min(CROP_W / natW, CROP_H / natH);
  const dispW = natW * scale;
  const dispH = natH * scale;
  return { natW, natH, dispX: (CROP_W - dispW) / 2, dispY: (CROP_H - dispH) / 2, dispW, dispH };
}

function initialCrop(shape: Shape, meta: ImgMeta): CropRect
{
  const { dispX, dispY, dispW, dispH } = meta;
  if (shape === 'rectangle')
  {
    const h = Math.min(dispH * 0.4, 80);
    const w = Math.min(h * 3, dispW * 0.8);
    return { x: dispX + (dispW - w) / 2, y: dispY + (dispH - h) / 2, w, h };
  }
  const side = Math.min(dispW, dispH) * 0.65;
  return { x: dispX + (dispW - side) / 2, y: dispY + (dispH - side) / 2, w: side, h: side };
}

function clampCrop(c: CropRect, meta: ImgMeta, shape: Shape): CropRect
{
  const { dispX, dispY, dispW, dispH } = meta;
  let { x, y, w, h } = c;
  if (shape !== 'rectangle')
  {
    const s = Math.max(MIN_SIZE, Math.min(w, h, dispW, dispH));
    w = s; h = s;
  }
  else // rectangle: enforce 3:1 to match 150x50 output
  {
    w = Math.max(MIN_SIZE, Math.min(w, dispW));
    h = Math.round(w / 3);
    if (h > dispH) { h = dispH; w = h * 3; }
    h = Math.max(Math.ceil(MIN_SIZE / 3), h);
  }
  x = Math.max(dispX, Math.min(x, dispX + dispW - w));
  y = Math.max(dispY, Math.min(y, dispY + dispH - h));
  return { x, y, w, h };
}

function applyInteraction(drag: DragState, mx: number, my: number, meta: ImgMeta, shape: Shape): CropRect
{
  const dx = mx - drag.startMX;
  const dy = my - drag.startMY;
  const { x: sx, y: sy, w: sw, h: sh } = drag.startCrop;
  const { dispX, dispY, dispW, dispH } = meta;

  if (drag.mode === 'move')
    return clampCrop({ x: sx + dx, y: sy + dy, w: sw, h: sh }, meta, shape);

  const hid = drag.handle!;
  const goLeft  = hid.includes('w');
  const goRight = hid.includes('e');
  const goUp    = hid.includes('n');
  const goDown  = hid.includes('s');

  // Raw new dimensions from mouse delta
  let nw = goRight ? sw + dx : goLeft ? sw - dx : sw;
  let nh = goDown  ? sh + dy : goUp   ? sh - dy : sh;

  if (shape === 'circle' || shape === 'square')
  {
    // Pick the side from whichever axis is being dragged (corners: larger delta wins)
    let side: number;
    if ((goLeft || goRight) && (goUp || goDown))
      side = Math.abs(dx) >= Math.abs(dy) ? nw : nh;
    else if (goLeft || goRight)
      side = nw;
    else
      side = nh;

    // Cap to available space from the FIXED edge so the crop never crosses the image boundary
    if (goLeft)  side = Math.min(side, sx + sw - dispX);
    if (goRight) side = Math.min(side, dispX + dispW - sx);
    if (goUp)    side = Math.min(side, sy + sh - dispY);
    if (goDown)  side = Math.min(side, dispY + dispH - sy);

    // For pure vertical handles: x is fixed at sx — cap width to right image edge
    if (!goLeft && !goRight) side = Math.min(side, dispX + dispW - sx);
    // For pure horizontal handles: y is fixed at sy — cap height to bottom image edge
    if (!goUp && !goDown)   side = Math.min(side, dispY + dispH - sy);

    side = Math.max(MIN_SIZE, side);

    // Anchor the fixed edge; let the dragged edge float
    const nx = goLeft ? sx + sw - side : sx;
    const ny = goUp   ? sy + sh - side : sy;
    return { x: nx, y: ny, w: side, h: side };
  }
  else // rectangle: maintain 3:1
  {
    if ((goUp || goDown) && !goLeft && !goRight)
    {
      // Pure vertical handle: height-primary → derive width
      if (goUp)   nh = Math.min(nh, sy + sh - dispY);
      if (goDown) nh = Math.min(nh, dispY + dispH - sy);
      nh = Math.max(Math.ceil(MIN_SIZE / 3), nh);
      nw = Math.min(nh * 3, dispW);
      nh = Math.round(nw / 3);
      const ny = goUp ? sy + sh - nh : sy;
      return clampCrop({ x: sx, y: ny, w: nw, h: nh }, meta, shape);
    }
    else
    {
      // Horizontal or corner: width-primary → derive height
      if (goLeft)  nw = Math.min(nw, sx + sw - dispX);
      if (goRight) nw = Math.min(nw, dispX + dispW - sx);
      nw = Math.max(MIN_SIZE, nw);
      nh = Math.round(nw / 3);
      if (goUp)   nh = Math.min(nh, sy + sh - dispY);
      if (goDown) nh = Math.min(nh, dispY + dispH - sy);
      nh = Math.max(Math.ceil(MIN_SIZE / 3), nh);
      nw = nh * 3;
      const nx = goLeft ? sx + sw - nw : sx;
      const ny = goUp   ? sy + sh - nh : sy;
      return clampCrop({ x: nx, y: ny, w: nw, h: nh }, meta, shape);
    }
  }
}

const PREVIEW_SIZE = 80;

function renderPreview(canvas: HTMLCanvasElement, img: HTMLImageElement, crop: CropRect, meta: ImgMeta, shape: Shape)
{
  // Always render to a square canvas so the shape shows completely
  canvas.width = PREVIEW_SIZE;
  canvas.height = PREVIEW_SIZE;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);

  // For rectangle (3:1), letterbox it centered inside the square
  const drawX = 0;
  const drawW = PREVIEW_SIZE;
  const drawH = shape === 'rectangle' ? Math.round(PREVIEW_SIZE / 3) : PREVIEW_SIZE;
  const drawY = Math.round((PREVIEW_SIZE - drawH) / 2);

  const sx = (crop.x - meta.dispX) * (meta.natW / meta.dispW);
  const sy = (crop.y - meta.dispY) * (meta.natH / meta.dispH);
  const sw = crop.w * (meta.natW / meta.dispW);
  const sh = crop.h * (meta.natH / meta.dispH);

  ctx.save();
  if (shape === 'circle')
  {
    ctx.beginPath();
    ctx.arc(PREVIEW_SIZE / 2, PREVIEW_SIZE / 2, PREVIEW_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
  }
  else if (shape === 'rectangle')
  {
    ctx.beginPath();
    ctx.rect(drawX, drawY, drawW, drawH);
    ctx.clip();
  }
  ctx.drawImage(img, sx, sy, sw, sh, drawX, drawY, drawW, drawH);
  ctx.restore();
}

function renderExport(img: HTMLImageElement, crop: CropRect, meta: ImgMeta, shape: Shape): HTMLCanvasElement
{
  const outW = shape === 'rectangle' ? 150 : 100;
  const outH = shape === 'rectangle' ? 50 : 100;
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, outW, outH);
  const sx = (crop.x - meta.dispX) * (meta.natW / meta.dispW);
  const sy = (crop.y - meta.dispY) * (meta.natH / meta.dispH);
  const sw = crop.w * (meta.natW / meta.dispW);
  const sh = crop.h * (meta.natH / meta.dispH);
  ctx.save();
  if (shape === 'circle')
  {
    ctx.beginPath();
    ctx.arc(outW / 2, outH / 2, outW / 2, 0, Math.PI * 2);
    ctx.clip();
  }
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
  ctx.restore();
  return canvas;
}

function drawOverlay(canvas: HTMLCanvasElement, crop: CropRect, shape: Shape)
{
  canvas.width = CROP_W;
  canvas.height = CROP_H;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(0, 0, CROP_W, CROP_H);

  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = 'rgba(0,0,0,1)';
  const { x, y, w, h } = crop;
  if (shape === 'circle')
  {
    ctx.beginPath();
    ctx.arc(x + w / 2, y + h / 2, w / 2, 0, Math.PI * 2);
    ctx.fill();
  }
  else
  {
    ctx.fillRect(x, y, w, h);
  }

  ctx.globalCompositeOperation = 'source-over';
  ctx.strokeStyle = '#62aacb';
  ctx.lineWidth = 2;
  if (shape === 'circle')
  {
    ctx.beginPath();
    ctx.arc(x + w / 2, y + h / 2, w / 2, 0, Math.PI * 2);
    ctx.stroke();
  }
  else
  {
    ctx.strokeRect(x + 1, y + 1, w - 2, h - 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + w / 3, y);     ctx.lineTo(x + w / 3, y + h);
    ctx.moveTo(x + 2 * w / 3, y); ctx.lineTo(x + 2 * w / 3, y + h);
    ctx.moveTo(x, y + h / 3);     ctx.lineTo(x + w, y + h / 3);
    ctx.moveTo(x, y + 2 * h / 3); ctx.lineTo(x + w, y + 2 * h / 3);
    ctx.stroke();
  }
}

// ─── Image Editor Modal ───────────────────────────────────────────────────────

interface ImageEditorProps {
  currentImageUrl: string;
  currentShape: Shape;
  initialFile?: File;
  onSave: (imageUrl: string, shape: Shape) => void;
  onClose: () => void;
}

const ImageEditorModal: React.FC<ImageEditorProps> = ({
  currentImageUrl,
  currentShape,
  initialFile,
  onSave,
  onClose,
}) =>
{
  const [shape, setShape] = useState<Shape>(currentShape);
  const [file, setFile] = useState<File | null>(initialFile ?? null);
  const [imgSrc, setImgSrc] = useState<string>(() =>
    initialFile ? URL.createObjectURL(initialFile) : currentImageUrl,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgMeta, setImgMeta] = useState<ImgMeta | null>(null);
  const [crop, setCrop] = useState<CropRect>({ x: 0, y: 0, w: 0, h: 0 });

  const imgRef = useRef<HTMLImageElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const previewRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);

  // Load image, compute meta and initial crop
  useEffect(() =>
  {
    if (!imgSrc) return;
    setImgLoaded(false);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () =>
    {
      imgRef.current = img;
      const meta = computeImgMeta(img.naturalWidth, img.naturalHeight);
      setImgMeta(meta);
      setCrop(initialCrop(shape, meta));
      setImgLoaded(true);
    };
    img.src = imgSrc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgSrc]);

  // Reset crop box when shape changes
  useEffect(() =>
  {
    if (!imgMeta || !imgLoaded) return;
    setCrop(initialCrop(shape, imgMeta));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape]);

  // Redraw overlay canvas
  useEffect(() =>
  {
    if (!imgLoaded || !overlayRef.current || !crop.w) return;
    drawOverlay(overlayRef.current, crop, shape);
  }, [crop, shape, imgLoaded]);

  // Redraw preview canvas
  useEffect(() =>
  {
    if (!imgLoaded || !previewRef.current || !imgRef.current || !imgMeta || !crop.w) return;
    renderPreview(previewRef.current, imgRef.current, crop, imgMeta, shape);
  }, [crop, shape, imgLoaded, imgMeta]);

  // Global mouse move/up for drag and resize
  useEffect(() =>
  {
    const onMouseMove = (e: MouseEvent) =>
    {
      if (!dragRef.current || !imgMeta || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setCrop(applyInteraction(dragRef.current, e.clientX - rect.left, e.clientY - rect.top, imgMeta, shape));
    };
    const onMouseUp = () => { dragRef.current = null; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () =>
    {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [shape, imgMeta]);

  const startInteraction = useCallback(
    (e: React.MouseEvent, mode: 'move' | 'resize', handle: HandleId | null) =>
    {
      e.preventDefault();
      e.stopPropagation();
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      dragRef.current = {
        mode, handle,
        startMX: e.clientX - rect.left,
        startMY: e.clientY - rect.top,
        startCrop: { ...crop },
      };
    },
    [crop],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setImgSrc(URL.createObjectURL(f));
    e.target.value = '';
  };

  const handleSave = async () =>
  {
    if (!imgRef.current || !imgMeta || !imgLoaded) return;
    setSaving(true);
    setError(null);
    try
    {
      const outW = shape === 'rectangle' ? 150 : 100;
      const outH = shape === 'rectangle' ? 50 : 100;
      const exportCanvas = renderExport(imgRef.current, crop, imgMeta, shape);
      const blob = await new Promise<Blob>((resolve, reject) =>
        exportCanvas.toBlob(
          b => (b ? resolve(b) : reject(new Error('Canvas empty'))),
          'image/png',
        ),
      );
      const fd = new FormData();
      const filename = file?.name ?? 'brand-image.png';
      fd.append('file', blob, filename);
      fd.append('filename', filename);
      fd.append('typeShape', shape);
      fd.append('width', String(outW));
      fd.append('height', String(outH));
      const newUrl = await adminConsoleService.uploadBrandImage(fd);
      onSave(newUrl, shape);
      onClose();
    }
    catch
    {
      setError('Failed to upload image. Please try again.');
      setSaving(false);
    }
  };

  return createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/30">
      <div
        className="bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden"
        style={{ width: 660, maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e0e0e0] flex-shrink-0">
          <h2 className="text-[16px] font-normal text-[#5697b3]">Custom Branding — Image Editor</h2>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 flex gap-5 items-start">
          {/* Crop area */}
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <p className="text-[11px] text-[#888]">Drag selection to reposition · Drag handles to resize</p>
            <div
              ref={containerRef}
              className="relative bg-[#1a1a1a] overflow-hidden select-none"
              style={{ width: CROP_W, height: CROP_H }}
            >
              {/* Source image */}
              {imgSrc && imgMeta && (
                <img
                  src={imgSrc}
                  alt=""
                  draggable={false}
                  className="absolute pointer-events-none"
                  style={{
                    left: imgMeta.dispX,
                    top: imgMeta.dispY,
                    width: imgMeta.dispW,
                    height: imgMeta.dispH,
                  }}
                />
              )}

              {/* Dark overlay with shape hole */}
              <canvas
                ref={overlayRef}
                className="absolute inset-0 pointer-events-none"
                style={{ zIndex: 1 }}
              />

              {/* Drag + resize interaction layer */}
              {imgLoaded && crop.w > 0 && (
                <div
                  className="absolute"
                  style={{
                    left: crop.x,
                    top: crop.y,
                    width: crop.w,
                    height: crop.h,
                    cursor: 'move',
                    zIndex: 2,
                  }}
                  onMouseDown={e => startInteraction(e, 'move', null)}
                >
                  {HANDLES.map(hp => (
                    <div
                      key={hp.id}
                      className="absolute bg-white border-2 border-[#62aacb]"
                      style={{ ...hp.style, width: 10, height: 10, cursor: hp.cursor, zIndex: 3 }}
                      onMouseDown={e => startInteraction(e, 'resize', hp.id)}
                    />
                  ))}
                </div>
              )}

              {/* Empty state */}
              {!imgSrc && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                  <span className="material-icons text-[48px] text-[#444]">image</span>
                  <span className="text-[12px] text-[#666]">Click the pencil to select an image</span>
                </div>
              )}

              {/* File picker trigger */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-2 right-2 bg-white/80 hover:bg-white rounded-full w-8 h-8 flex items-center justify-center shadow"
                style={{ zIndex: 4 }}
                title="Select image"
              >
                <span className="material-icons text-[15px] text-[#555]">edit</span>
              </button>
            </div>
          </div>

          {/* Right panel: shape selector + live preview */}
          <div className="flex flex-col gap-3" style={{ minWidth: 140 }}>
            <p className="text-[12px] text-[#555] font-medium">Select the image shape:</p>
            {(['circle', 'square', 'rectangle'] as Shape[]).map(s => (
              <label key={s} className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="radio"
                  name="imgshape"
                  checked={shape === s}
                  onChange={() => setShape(s)}
                  className="accent-[#62aacb]"
                />
                <span className="text-[12px] text-[#333] capitalize">{s}</span>
              </label>
            ))}

            {imgLoaded && (
              <div className="mt-2">
                <p className="text-[11px] text-[#aaa] mb-1.5">Preview</p>
                <div
                  className="bg-[#eee]"
                  style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
                >
                  <canvas
                    ref={previewRef}
                    style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE, display: 'block' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 mb-2 px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828] flex-shrink-0">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-[#e0e0e0] px-5 py-3 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-[12px] border border-[#ddd] rounded text-[#555] hover:bg-[#f5f5f5] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !imgLoaded}
            className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] transition-colors disabled:opacity-50"
          >
            {saving ? 'Uploading…' : 'Save'}
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>,
    document.body,
  );
};

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export const CustomBrandingTab: React.FC = () =>
{
  const [isEnabled, setIsEnabled] = useState(false);
  const [brandName, setBrandName] = useState('');
  const [brandImage, setBrandImage] = useState('');
  const [shape, setShape] = useState<Shape>('circle');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [savingName, setSavingName] = useState(false);

  const dispatchBrandingUpdate = useCallback(
    (
      patch: Partial<{
        isEnabledCustomBranding: boolean;
        brandName: string;
        brandImage: string;
        shape: string;
      }>,
    ) =>
    {
      window.dispatchEvent(new CustomEvent('C2_branding_changed', { detail: patch }));
    },
    [],
  );

  const [showEditor, setShowEditor] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | undefined>(undefined);
  const filePickerRef = useRef<HTMLInputElement>(null);

  const handleImageClick = () => filePickerRef.current?.click();

  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
    setShowEditor(true);
    e.target.value = '';
  };

  useEffect(() =>
  {
    dashboardService
      .getIsBeta()
      .then(data =>
      {
        setIsEnabled(data.isEnabledCustomBranding);
        setBrandName(data.brandName ?? '');
        setBrandImage(data.brandImage ?? '');
        setShape((data.shape as Shape) || 'circle');
        localStorage.setItem(STORAGE_KEYS.IS_ENABLED_CUSTOM_BRANDING, String(!!data.isEnabledCustomBranding));
        localStorage.setItem(STORAGE_KEYS.BRAND_NAME, data.brandName ?? '');
        localStorage.setItem(STORAGE_KEYS.BRAND_IMAGE, data.brandImage ?? '');
        localStorage.setItem(STORAGE_KEYS.BRAND_SHAPE, data.shape ?? 'circle');
      })
      .catch(() => setError('Failed to load branding settings.'))
      .finally(() => setLoading(false));
  }, []);

  // Keep this tab in sync when branding changes elsewhere (e.g. another admin via socket)
  useEffect(() =>
  {
    const handler = (e: Event) =>
    {
      const patch = (e as CustomEvent).detail as Partial<{
        isEnabledCustomBranding: boolean;
        brandName: string;
        brandImage: string;
        shape: string;
      }>;
      if (patch.isEnabledCustomBranding !== undefined) setIsEnabled(patch.isEnabledCustomBranding);
      if (patch.brandName !== undefined) setBrandName(patch.brandName);
      if (patch.brandImage !== undefined) setBrandImage(patch.brandImage);
      if (patch.shape !== undefined) setShape((patch.shape as Shape) || 'circle');
    };
    window.addEventListener('C2_branding_changed', handler);
    return () => window.removeEventListener('C2_branding_changed', handler);
  }, []);

  const handleToggle = useCallback(async () =>
  {
    const next = !isEnabled;
    setIsEnabled(next);
    localStorage.setItem(STORAGE_KEYS.IS_ENABLED_CUSTOM_BRANDING, String(next));
    dispatchBrandingUpdate({ isEnabledCustomBranding: next });
    try
    {
      await adminConsoleService.updateCustomBranding(next);
    }
    catch
    {
      setIsEnabled(!next);
      localStorage.setItem(STORAGE_KEYS.IS_ENABLED_CUSTOM_BRANDING, String(!next));
      dispatchBrandingUpdate({ isEnabledCustomBranding: !next });
      setError('Failed to update branding setting.');
    }
  }, [isEnabled, dispatchBrandingUpdate]);

  const commitName = useCallback(async () =>
  {
    if (!draftName.trim())
    {
      setEditing(false);
      return;
    }
    setSavingName(true);
    try
    {
      await adminConsoleService.updateBrandName(draftName.trim());
      setBrandName(draftName.trim());
      localStorage.setItem(STORAGE_KEYS.BRAND_NAME, draftName.trim());
      dispatchBrandingUpdate({ brandName: draftName.trim() });
      setEditing(false);
    }
    catch
    {
      setError('Failed to update brand name.');
    }
    finally
    {
      setSavingName(false);
    }
  }, [draftName, dispatchBrandingUpdate]);

  const startEdit = () =>
  {
    setDraftName(brandName);
    setEditing(true);
  };

  const handleImageSaved = useCallback(
    (newUrl: string, newShape: Shape) =>
    {
      setBrandImage(newUrl);
      setShape(newShape);
      localStorage.setItem(STORAGE_KEYS.BRAND_IMAGE, newUrl);
      localStorage.setItem(STORAGE_KEYS.BRAND_SHAPE, newShape);
      dispatchBrandingUpdate({ brandImage: newUrl, shape: newShape });
    },
    [dispatchBrandingUpdate],
  );

  if (loading)
  {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="material-icons animate-spin text-[#62aacb] text-[32px]">refresh</span>
      </div>
    );
  }

  return (
    <div className="px-6 py-5 max-w-[640px]">
      <h3 className="text-[16px] font-normal text-[#5697b3] mb-0.5">Custom Branding</h3>
      <p className="text-[12px] text-[#5697b3] dark:text-[#beb7ac] mb-4">
        Customize your Corgee by adding your brand's name and logo.
      </p>

      {error && (
        <div className="mb-4 px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="material-icons text-[14px]">error_outline</span>
            {error}
          </div>
          <button onClick={() => setError(null)}>
            <span className="material-icons text-[14px]">close</span>
          </button>
        </div>
      )}

      <div className="border border-[#ddd] rounded-sm bg-white shadow-sm mb-5">
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <p className="text-[12px] text-[#444] dark:text-[#beb7ac] font-medium leading-snug">
            Replace the Corgee logo with your own logo by following these steps:
          </p>
          <button
            role="switch"
            aria-checked={isEnabled}
            onClick={handleToggle}
            className={`relative inline-flex h-[22px] w-[40px] flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ml-4 ${
              isEnabled ? 'bg-[#62aacb]' : 'bg-[#b0b0b0]'
            }`}
            title={isEnabled ? 'Disable custom branding' : 'Enable custom branding'}
          >
            <span
              className={`inline-block h-[18px] w-[18px] transform rounded-full bg-white shadow transition-transform ${
                isEnabled ? 'translate-x-[20px]' : 'translate-x-[2px]'
              }`}
            />
          </button>
        </div>

        <div className="px-4 pb-4 flex flex-col gap-2">
          <p className="text-[12px] text-[#5b7fa6] dark:text-[#beb7ac] leading-relaxed">
            First click on the pencil icon to select and upload an image. Then select the shape of
            the image you would like to use: round, square or rectangular. The recommended file size
            for round and square shapes is 100x100 and 150x50 for rectangular shapes.
          </p>
          <p className="text-[12px] text-[#5b7fa6] dark:text-[#beb7ac] leading-relaxed">
            Click on the blue switch button in this section to enable/disable your set up.
          </p>
          <p className="text-[12px] text-[#5b7fa6] dark:text-[#beb7ac] leading-relaxed">
            You can also change the brand name by double clicking on the name next to the image.
          </p>
        </div>

        <div className="flex items-center px-4">
          <div className="flex-1 border-t border-[#e8e8e8]" />
        </div>

        <div className="flex items-center gap-4 px-4 py-4 bg-[#fafafa] dark:bg-[transparent]">
          <div
            className="relative flex-shrink-0 overflow-hidden group cursor-pointer"
            style={{
              width: shape === 'rectangle' ? '100px' : '64px',
              height: shape === 'rectangle' ? '33px' : '64px',
              borderRadius: shapeToCssRadius(shape),
            }}
            onClick={handleImageClick}
            title="Click to upload an image"
          >
            {brandImage ? (
              <img
                src={resolveBrandImageUrl(brandImage)}
                alt="Brand"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-[#e0e0e0] flex items-center justify-center text-[#aaa]">
                <span className="material-icons text-[24px]">image</span>
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
              <span className="material-icons text-white text-[14px]">edit</span>
            </div>
          </div>

          {editing ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                autoFocus
                type="text"
                value={draftName}
                onChange={e => setDraftName(e.target.value)}
                onKeyDown={e =>
                {
                  if (e.key === 'Enter') commitName();
                  if (e.key === 'Escape') setEditing(false);
                }}
                className="flex-1 border border-[#62aacb] rounded px-2.5 py-1 text-[13px] outline-none bg-white"
              />
              <button
                onClick={commitName}
                disabled={savingName}
                className="px-3 py-1 bg-[#62aacb] text-white rounded text-[12px] hover:bg-[#4e99ba] transition-colors disabled:opacity-50"
              >
                {savingName ? '…' : 'Save'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="px-2 py-1 border border-[#ddd] rounded text-[12px] text-[#555] hover:bg-[#f0f0f0]"
              >
                Cancel
              </button>
            </div>
          ) : (
            <span
              className="text-[13px] text-[#444] cursor-pointer hover:text-[#5697b3] transition-colors select-none truncate max-w-[360px]"
              onDoubleClick={startEdit}
              title={`${brandName || 'Corgee'} — Double-click to edit brand name`}
            >
              {brandName || 'Corgee'}
            </span>
          )}
        </div>
      </div>

      <input
        ref={filePickerRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFilePicked}
      />

      {showEditor && (
        <ImageEditorModal
          currentImageUrl={brandImage}
          currentShape={shape}
          initialFile={pendingFile}
          onSave={handleImageSaved}
          onClose={() =>
          {
            setShowEditor(false);
            setPendingFile(undefined);
          }}
        />
      )}
    </div>
  );
};
